import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { Event } from "@/types/event";
import { useIsMobile } from "@/hooks/use-mobile";

interface UnifiedCalendarProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
  className?: string;
}

export const UnifiedCalendar = ({ events, onEventClick, className }: UnifiedCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const isMobile = useIsMobile();

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get events for each day
  const eventsByDate = useMemo(() => {
    const map = new Map<string, Event[]>();
    
    events.forEach(event => {
      // Check rsvpDate
      if (event.rsvpDate) {
        try {
          const dateKey = format(parseISO(event.rsvpDate), 'yyyy-MM-dd');
          if (!map.has(dateKey)) map.set(dateKey, []);
          map.get(dateKey)!.push(event);
        } catch (e) {
          console.error('Error parsing event date:', e);
        }
      }
      
      // Also check deadline
      if (event.deadline) {
        try {
          const dateKey = format(new Date(event.deadline), 'yyyy-MM-dd');
          if (!map.has(dateKey)) map.set(dateKey, []);
          if (!map.get(dateKey)!.find(e => e.id === event.id)) {
            map.get(dateKey)!.push(event);
          }
        } catch (e) {
          console.error('Error parsing deadline:', e);
        }
      }
    });
    
    return map;
  }, [events]);

  const getEventsForDay = (day: Date) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return eventsByDate.get(dateKey) || [];
  };

  const handlePreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(new Date());

  const weekDays = isMobile 
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] 
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate padding days for the start of the month
  const firstDayOfWeek = monthStart.getDay();
  const paddingDays = Array(firstDayOfWeek).fill(null);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Event Calendar
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">View all your events in one place</CardDescription>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-1 sm:gap-2">
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "sm"} 
              onClick={handleToday}
              className="touch-manipulation text-xs sm:text-sm"
            >
              Today
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handlePreviousMonth}
              className="touch-manipulation h-8 w-8 sm:h-10 sm:w-10"
            >
              <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
            <div className="min-w-[100px] sm:min-w-[140px] text-center font-semibold text-xs sm:text-base">
              {isMobile ? format(currentMonth, 'MMM yyyy') : format(currentMonth, 'MMMM yyyy')}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleNextMonth}
              className="touch-manipulation h-8 w-8 sm:h-10 sm:w-10"
            >
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:px-6">
        {/* Calendar Grid */}
        <div className="min-w-full overflow-x-auto">
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 min-w-[280px]">
          {/* Week day headers */}
          {weekDays.map(day => (
            <div 
              key={day} 
              className="text-center text-[10px] sm:text-sm font-semibold text-muted-foreground py-1 sm:py-2"
            >
              {day}
            </div>
          ))}
          
          {/* Padding days */}
          {paddingDays.map((_, index) => (
            <div key={`padding-${index}`} className="aspect-square" />
          ))}
          
          {/* Calendar days */}
          {daysInMonth.map(day => {
            const dayEvents = getEventsForDay(day);
            const isCurrentDay = isToday(day);
            const hasEvents = dayEvents.length > 0;
            
            return (
              <div
                key={day.toString()}
                className={cn(
                  "aspect-square border rounded-md sm:rounded-lg p-0.5 sm:p-1 md:p-2 transition-all cursor-pointer touch-manipulation",
                  "hover:bg-muted/50 active:scale-95",
                  isCurrentDay && "border-primary bg-primary/5 border-2",
                  !isSameMonth(day, currentMonth) && "opacity-40",
                  hasEvents && "hover:shadow-sm"
                )}
                onClick={() => {
                  if (hasEvents && onEventClick) {
                    onEventClick(dayEvents[0]);
                  }
                }}
              >
                <div className={cn(
                  "text-[10px] sm:text-xs md:text-sm font-medium mb-0.5 sm:mb-1 text-center sm:text-left",
                  isCurrentDay && "text-primary font-bold"
                )}>
                  {format(day, 'd')}
                </div>
                
                {/* Event indicators */}
                {hasEvents && (
                  <div className="space-y-0.5">
                    {isMobile ? (
                      // Mobile: Show dots for events
                      <div className="flex gap-0.5 justify-center">
                        {dayEvents.slice(0, 3).map((event, idx) => (
                          <div
                            key={event.id}
                            className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-primary"
                            title={event.name}
                          />
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-primary/50" />
                        )}
                      </div>
                    ) : (
                      // Desktop: Show event names
                      <>
                        {dayEvents.slice(0, 2).map(event => (
                          <div
                            key={event.id}
                            className="text-[9px] sm:text-[10px] md:text-xs truncate bg-primary/10 text-primary px-0.5 sm:px-1 rounded leading-tight"
                            title={event.name}
                          >
                            {event.name}
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-[9px] sm:text-[10px] text-muted-foreground">
                            +{dayEvents.length - 2}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t flex flex-wrap gap-2 sm:gap-4 text-[10px] sm:text-xs text-muted-foreground">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded bg-primary/10 border border-primary" />
            <span>Events</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded border-2 border-primary" />
            <span>Today</span>
          </div>
          {isMobile && (
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>= 1 event</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
